import React, { useMemo } from 'react';
import { View, StyleSheet, Platform } from 'react-native';
import { useTheme } from '@/context/ThemeContext';
import { useTranslation } from '@/hooks/useTranslation';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import { OUTPUT_TYPE_ICON, getResultHintKey } from '@/components/Tools/toolDetailHelpers';

/**
 * Placeholder shown in the Result column before the user has run anything.
 *
 * Visually fills the column (so when displayed side-by-side with the form it
 * reads as a generous "canvas waiting to be filled"). Picks an output-type
 * icon + hint so the user knows exactly what kind of artifact is coming.
 */
function ToolEmptyResult({ tool, palette, fillColumn }) {
  const { colors } = useTheme();
  const { t } = useTranslation();

  const styles = useMemo(() => StyleSheet.create({
    card: {
      paddingVertical: 36,
      paddingHorizontal: 24,
      borderRadius: 14,
      borderWidth: 1,
      borderStyle: 'dashed',
      borderColor: colors.border_color_secondary,
      backgroundColor: colors.solid_color_block,
      alignItems: 'center',
      justifyContent: 'center',
      gap: 10,
      overflow: 'hidden',
      minHeight: 320,
      flex: fillColumn ? 1 : undefined,
    },
    glow: {
      position: 'absolute',
      width: 280,
      height: 280,
      borderRadius: 280,
      backgroundColor: palette.bg,
      opacity: 0.45,
      top: -140,
      ...(Platform.OS === 'web' ? { filter: 'blur(70px)' } : null),
    },
    iconBox: {
      width: 64,
      height: 64,
      borderRadius: 16,
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: palette.bg,
      marginBottom: 6,
    },
    text: {
      textAlign: 'center',
      maxWidth: 360,
      lineHeight: 20,
    },
  }), [colors, palette, fillColumn]);

  const outputType = tool?.outputType;
  const hint = t(getResultHintKey(outputType)) || 'Your result will appear here.';

  return (
    <View style={styles.card}>
      <View style={styles.glow} pointerEvents='none' />
      <View style={styles.iconBox}>
        <Icon
          name={OUTPUT_TYPE_ICON[outputType] || 'wand-magic-sparkles'}
          size={28}
          color={palette.fg}
        />
      </View>
      <Text size='m' weight='bold' color='primary' style={styles.text}>
        {hint}
      </Text>
      <Text size='s' color='secondary' style={styles.text}>
        {t('pages.tools.fillFormHint')
          || 'Fill in the form and tap Run. You can re-run as many times as you like — every result is saved below.'}
      </Text>
    </View>
  );
}

export default React.memo(ToolEmptyResult);
